import React, { useState } from 'react'
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { 
  Home, 
  CreditCard, 
  Laptop, 
  Users, 
  Receipt, 
  User, 
  Settings, 
  HelpCircle,
  LogOut,
  Menu,
  X,
  Shield,
  FileText
} from 'lucide-react'

const Layout: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  
  let user: any = {}
  try {
    const userStr = localStorage.getItem('user')
    user = userStr ? JSON.parse(userStr) : {}
  } catch (e) {
    console.error('Error parsing user data:', e)
    localStorage.removeItem('user')
  }
  const isAdmin = user?.role === 'admin'

  const handleLogout = () => {
    localStorage.removeItem('token')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('user')
    navigate('/login')
  }

  const menuItems = [
    { path: '/dashboard', icon: Home, label: t('navigation.dashboard') },
    { path: '/licenses', icon: CreditCard, label: t('navigation.licenses') },
    { path: '/devices', icon: Laptop, label: t('navigation.devices') },
    { path: '/sessions', icon: Users, label: t('navigation.sessions') },
    { path: '/payments', icon: Receipt, label: t('navigation.payments') },
    { path: '/invoices', icon: FileText, label: 'Invoices' },
    { path: '/profile', icon: User, label: t('navigation.profile') },
    { path: '/settings', icon: Settings, label: t('navigation.settings') },
    { path: '/help', icon: HelpCircle, label: t('navigation.help') }
  ]

  if (isAdmin) {
    menuItems.push({ path: '/admin', icon: Shield, label: t('navigation.admin') })
  }

  return (
    <div className="min-h-screen bg-dark-900">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-dark-800 transform transition-transform duration-200 ease-in-out ${
        isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
      } lg:translate-x-0`}>
        <div className="flex items-center justify-between h-16 px-6 border-b border-dark-700">
          <div>
            <h1 className="text-xl font-bold text-primary">{t('app.title')}</h1>
            <p className="text-xs text-gray-400">{t('app.subtitle')}</p>
          </div>
          <button
            onClick={() => setIsMobileMenuOpen(false)}
            className="lg:hidden text-gray-400 hover:text-white"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <nav className="px-4 py-6">
          <ul className="space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon
              const isActive = location.pathname === item.path || 
                             (item.path === '/admin' && location.pathname.startsWith('/admin'))
              
              return (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                      isActive
                        ? 'bg-primary text-white'
                        : 'text-gray-300 hover:bg-dark-700 hover:text-white'
                    }`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <Icon className="h-5 w-5" />
                    <span>{item.label}</span>
                  </Link>
                </li>
              )
            })}
          </ul>
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4">
          <div className="bg-dark-700 rounded-lg p-4">
            <p className="text-sm text-gray-400">{user?.email || ''}</p>
            <p className="text-xs text-gray-500 capitalize">{user?.role || ''}</p>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center space-x-3 w-full px-4 py-3 mt-2 text-gray-300 hover:bg-dark-700 hover:text-white rounded-lg transition-colors"
          >
            <LogOut className="h-5 w-5" />
            <span>{t('navigation.logout')}</span>
          </button>
        </div>
      </aside>

      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-4 left-4 z-40">
        <button
          onClick={() => setIsMobileMenuOpen(true)}
          className="p-2 bg-dark-800 text-white rounded-lg"
        >
          <Menu className="h-6 w-6" />
        </button>
      </div>

      {/* Mobile menu overlay */}
      {isMobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black bg-opacity-50"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="lg:ml-64 min-h-screen">
        <div className="p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  )
}

export default Layout